import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_PROJECT_KIND_DEFS,
  AGENCY_PROJECT_KIND_MAP,
  PROJECT_COLORS,
  type AgencyProjectKind,
  type AgencyProjectView,
  type AgencyTemplateView,
  type PortfolioView,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { WORDS, date, plural } from '../../lib/format';
import { useDebounced } from '../../lib/hooks';
import { Icon, type IconName } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { AccountPicker } from '../../components/RecordPicker';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';

/**
 * مشاريع الوكالة.
 *
 * كل مشروعٍ لوحةُ عملٍ بأقسامه ومهامه — لعميلٍ أو داخليّ. البطاقة تحمل
 * تقدّمه من عدّاد مهامه الفعلية لا من نسبةٍ تُدخل يدويًّا.
 */
export function AgencyProjects() {
  const queryClient = useQueryClient();
  const { byId } = useTeamDirectory();
  const { atLeast } = useAuth();
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const projects = useQuery({
    queryKey: ['agency', 'projects', showArchived],
    queryFn: () =>
      api<AgencyProjectView[]>(`/agency/projects${qs({ archived: showArchived || undefined })}`),
  });

  const rows = (projects.data ?? []).filter((p) => (showArchived ? true : !p.archived));

  return (
    <div className="page">
      <PageHead
        title="المشاريع"
        subtitle="لوحات عمل الفريق — مشاريع العملاء والمشاريع الداخلية"
        actions={
          <>
            <label className="row tiny mute" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              إظهار المؤرشفة
            </label>
            {atLeast('MANAGER') && (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                <Icon name="plus" size={15} />
                مشروع جديد
              </button>
            )}
          </>
        }
      />

      {projects.isError && (
        <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
      )}
      {projects.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {projects.data && rows.length === 0 && (
        <EmptyState
          title="لا مشاريع بعد"
          text="أنشئ أول مشروع لتنظيم مهام الفريق — أو فعّل عقدًا فيُنشأ مشروع انضمام عميله تلقائيًّا."
          action={
            atLeast('MANAGER') ? (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                أنشئ مشروعًا
              </button>
            ) : undefined
          }
        />
      )}

      <div className="ag-projects">
        {rows.map((p) => {
          const pct = p.taskCount ? Math.round((p.doneCount / p.taskCount) * 100) : 0;
          return (
            <Link
              key={p.id}
              to={`/agency/projects/${p.id}`}
              className={`ag-project-card${p.archived ? ' is-archived' : ''}${
                p.overdueCount > 0 ? ' is-late' : ''
              }`}
            >
              <span className="ag-project-bar" style={{ background: p.color }} />
              <div className="ag-project-body">
                {/*
                  وجهُ العميل أوّلًا.
                  الصورةُ نفسُها التي في ملفّه — لا حرفٌ أوّلُ هنا وصورةٌ هناك:
                  من يمسح لوحًا فيه عشرون مشروعًا يعرف عميلَه بوجهه قبل اسمه.
                */}
                <div className="ag-project-top">
                  {p.accountName ? (
                    <Avatar name={p.accountName} src={p.accountAvatarUrl} size={38} />
                  ) : (
                    <span className="ag-project-int" style={{ background: `${p.color}1a`, color: p.color }}>
                      <Icon name="briefcase" size={17} />
                    </span>
                  )}
                  <div className="ag-project-id">
                    <strong className="ag-project-name">{p.name}</strong>
                    <span className="tiny mute">
                      {p.accountName ?? 'مشروع داخليّ'}
                      {p.contractNumber ? ` · ${p.contractNumber}` : ''}
                    </span>
                  </div>
                  <Badge kind={AGENCY_PROJECT_KIND_MAP[p.kind]?.kind}>
                    {AGENCY_PROJECT_KIND_MAP[p.kind]?.label ?? p.kind}
                  </Badge>
                </div>

                {(p.portfolioName || p.templateName) && (
                  <div className="ag-project-tags">
                    {p.portfolioName && (
                      <span className="ag-ctx-chip">
                        <span
                          className="ag-dot"
                          style={{ background: p.portfolioColor ?? undefined }}
                        />
                        {p.portfolioName}
                      </span>
                    )}
                    {p.templateName && (
                      <span className="ag-ctx-chip">
                        <Icon name="gantt" size={12} />
                        {p.templateName}
                      </span>
                    )}
                  </div>
                )}

                {p.description && <p className="tiny mute ag-clamp">{p.description}</p>}

                <div className="ag-progress" title={`${p.doneCount}/${p.taskCount}`}>
                  <span
                    className="ag-progress-fill"
                    style={{ width: `${pct}%`, background: p.color }}
                  />
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="tiny mute num">
                    {p.doneCount}/{p.taskCount} · {pct}٪
                  </span>
                  <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                    {p.overdueCount > 0 && (
                      <span className="ag-chip ag-chip-late num">{p.overdueCount} متأخّرة</span>
                    )}
                    {p.overdueCount === 0 && p.nextDueAt && (
                      <span className="tiny mute num">القادم {date(p.nextDueAt)}</span>
                    )}
                    {p.ownerId && byId.get(p.ownerId) && (
                      <span title={`المالك: ${byId.get(p.ownerId)!.name}`}>
                        <Avatar
                          name={byId.get(p.ownerId)!.name}
                          src={byId.get(p.ownerId)!.avatarUrl}
                          size={22}
                        />
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {creating && (
        <NewProjectModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['agency'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * مشروع جديد — البوّابة قبل الاسم.
 *
 * النوع أول سؤال: تعاقديٌّ فيُطلب عقدٌ ساري المفعول (والقائمة لا تعرض
 * غيره)، أو تجريبيّ/مجّانيّ فعميلٌ بلا عقد، أو داخليّ فلا عميل أصلًا.
 * الخادم يعيد الفحص — النافذة تشرح والحارس يحرس.
 */
/** عقدٌ مختار: بياناته وعميله معًا — العميل يُشتق ولا يُسأل عنه ثانية. */
interface PickedContract {
  id: string;
  number: string;
  title: string;
  accountId: string;
  accountName: string;
}

/**
 * اختيار عقدٍ ساري المفعول — بحثًا برقمه أو عنوانه.
 *
 * التعاقديّ يبدأ من العقد لا من العميل: العقد هو سند المشروع، وعميله
 * يأتي معه. والقائمة لا تعرض إلا الساري — فالبوّابة تمنع الخطأ قبل رسالته.
 */
function ContractPicker({ onPick }: { onPick: (c: PickedContract) => void }) {
  const [q, setQ] = useState('');
  const term = useDebounced(q);

  const results = useQuery({
    queryKey: ['contract-picker', term],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          number: string;
          title: string;
          account: { id: string; name: string } | null;
        }>;
      }>(`/sales/contracts${qs({ status: 'ACTIVE', q: term.trim() || undefined, pageSize: 10 })}`),
  });

  const rows = results.data?.items ?? [];

  return (
    <div>
      <div className="field">
        <label className="label" htmlFor="cp-q">
          العقد الساري
        </label>
        <input
          id="cp-q"
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث برقم العقد أو عنوانه أو اتركه لعرض الأحدث"
          autoComplete="off"
        />
      </div>

      {results.isPending ? (
        <div className="skeleton mt-2" style={{ height: 100 }} />
      ) : rows.length === 0 ? (
        <p className="tiny ag-late-text mt-2">
          لا عقود سارية{term.trim() ? ' تطابق البحث' : ''}. وقّع العقد وفعّله من المبيعات أولًا —
          أو أنشئ مشروعًا تجريبيًّا إن كان العمل قبل التوقيع.
        </p>
      ) : (
        <div className="pick-list mt-2">
          {rows.map((c) => (
            <button
              key={c.id}
              type="button"
              className="pick-row"
              onClick={() =>
                c.account &&
                onPick({
                  id: c.id,
                  number: c.number,
                  title: c.title,
                  accountId: c.account.id,
                  accountName: c.account.name,
                })
              }
            >
              <Icon name="clipboard" size={18} />
              <span className="pick-row-text">
                <strong className="num">
                  {c.number} — {c.title}
                </strong>
                <small>{c.account?.name}</small>
              </span>
              <Icon name="chevronLeft" size={15} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * مشروع جديد — البوّابة قبل الاسم.
 *
 * النوع أول سؤال. التعاقديّ يبدأ من العقد الساري وعميله يُشتق منه؛
 * والتجريبيّ/المجّانيّ من العميل؛ والداخليّ بلا أيّهما. الخادم يعيد
 * الفحص — النافذة تشرح والحارس يحرس.
 */
export function NewProjectModal({
  onClose,
  onCreated,
  presetPortfolioId,
  presetPortfolioName,
}: {
  onClose: () => void;
  onCreated: () => void;
  /** حين يُفتح من داخل محفظةٍ: تُثبَّت محفظةُ المشروع عليها. */
  presetPortfolioId?: string;
  presetPortfolioName?: string;
}) {
  const toast = useToast();
  const { assignable } = useTeamDirectory();
  const [kind, setKind] = useState<AgencyProjectKind>('CONTRACT');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);
  const [ownerId, setOwnerId] = useState('');
  const [account, setAccount] = useState<{ id: string; name: string } | null>(null);
  const [contract, setContract] = useState<PickedContract | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [portfolioId, setPortfolioId] = useState(presetPortfolioId ?? '');

  const def = AGENCY_PROJECT_KIND_MAP[kind];

  /*
   * القالبُ يُسأل عنه عند الإنشاء لا بعده.
   *
   * أقسامُ اللوح وحقولُ التفاصيل تُنسخ لحظتَها، وتغييرُها بعد أن يكتب
   * الفريقُ فيها بياناتٍ أصعبُ بكثير من اختيارها الآن.
   */
  const templates = useQuery({
    queryKey: ['agency', 'templates', false],
    queryFn: () => api<AgencyTemplateView[]>('/settings/project-templates'),
  });
  const tplRows = templates.data ?? [];
  const defaultTpl = tplRows.find((t) => t.isDefault);

  /*
   * المحفظةُ تُختار هنا لا في صفحة المحافظ — موضعٌ واحدٌ للقرار.
   *
   * وفارغُها لا يعني «بلا محفظة» بل «محفظةُ القالب إن كانت له»: القالبُ
   * يحمل خطَّ عمله، فمن اختار «عملاء العقارات» اختار محفظتَها ضمنًا.
   */
  const portfolios = useQuery({
    queryKey: ['agency', 'portfolios'],
    queryFn: () => api<PortfolioView[]>('/agency/portfolios'),
  });
  const pfRows = portfolios.data ?? [];
  const tplPortfolio = tplRows.find((t) => t.id === (templateId || defaultTpl?.id))?.portfolioName;

  const create = useMutation({
    mutationFn: () =>
      api('/agency/projects', {
        method: 'POST',
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          color,
          kind,
          accountId:
            kind === 'CONTRACT'
              ? (contract?.accountId ?? null)
              : def.needsAccount
                ? (account?.id ?? null)
                : null,
          contractId: kind === 'CONTRACT' ? (contract?.id ?? null) : null,
          ownerId: ownerId || undefined,
          templateId: templateId || null,
          portfolioId: portfolioId || null,
        },
      }),
    onSuccess: onCreated,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإنشاء.'),
  });

  const canSubmit =
    name.trim().length >= 2 &&
    (kind === 'CONTRACT' ? Boolean(contract) : !def.needsAccount || Boolean(account)) &&
    !create.isPending;

  return (
    <Modal title="مشروع جديد" onClose={onClose} wide>
      {/* النوع أولًا — بوّابة الحوكمة */}
      <div className="ag-kind-grid mb-4">
        {AGENCY_PROJECT_KIND_DEFS.map((d) => (
          <button
            key={d.key}
            type="button"
            className={`ag-kind-card${kind === d.key ? ' is-active' : ''}`}
            onClick={() => {
              setKind(d.key);
              setContract(null);
              if (!AGENCY_PROJECT_KIND_MAP[d.key].needsAccount) setAccount(null);
            }}
            aria-pressed={kind === d.key}
          >
            <strong>
              <Badge kind={d.kind}>{d.label}</Badge>
            </strong>
            <span className="tiny mute">{d.hint}</span>
          </button>
        ))}
      </div>

      {/* التعاقديّ: العقد أولًا والعميل يُشتق منه */}
      {kind === 'CONTRACT' &&
        (contract ? (
          <div className="field">
            <span className="label">العقد الحاكم</span>
            <div className="ag-picked-contract">
              <Icon name="clipboard" size={16} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong className="num">{contract.number}</strong> — {contract.title}
                <span className="tiny mute"> · العميل: {contract.accountName}</span>
              </span>
              <button className="btn btn-sm" onClick={() => setContract(null)}>
                تغيير
              </button>
            </div>
            <span className="tiny mute">
              سلسلة الحوكمة: محتمل ← عميل ← عرض سعر ← عقد موقَّع مفعَّل ← مشروع.
            </span>
          </div>
        ) : (
          <ContractPicker
            onPick={(c) => {
              setContract(c);
              /* اسم المشروع من عنوان العقد — يبدأ صحيحًا ويُعدَّل بحرّية. */
              setName((v) => (v.trim() ? v : c.title));
            }}
          />
        ))}

      {/* التجريبيّ/المجّانيّ: عميلٌ قائم فقط */}
      {kind !== 'CONTRACT' &&
        def.needsAccount &&
        (account ? (
          <div className="field">
            <span className="label">العميل</span>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="strong">
                <Icon name="building" size={14} /> {account.name}
              </span>
              <button className="btn btn-sm" onClick={() => setAccount(null)}>
                تغيير
              </button>
            </div>
          </div>
        ) : (
          <AccountPicker onPick={(id, accName) => setAccount({ id, name: accName })} />
        ))}

      {/* القالب: أقسامُ اللوح وحقولُه ومهامُّه الجاهزة */}
      <div className="field">
        <span className="label">القالب</span>
        {tplRows.length === 0 ? (
          <p className="tiny mute">
            لا قوالب — سيُنشأ المشروع بالأقسام الأربعة الافتراضية.{' '}
            <Link to="/settings/project-templates">أنشئ قالبًا</Link> لتوحيد شكل مشاريعك.
          </p>
        ) : (
          <div className="tpl-pick">
            {tplRows.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`tpl-pick-btn${(templateId || defaultTpl?.id) === t.id ? ' is-active' : ''}`}
                onClick={() => setTemplateId(t.id)}
              >
                <span className="tpl-icon" style={{ background: `${t.color}1a`, color: t.color }}>
                  <Icon name={t.icon as IconName} size={16} />
                </span>
                <span className="tpl-pick-body">
                  <b>{t.name}</b>
                  <span className="tiny mute">
                    {plural(t.sectionCount, WORDS.section)} · {plural(t.fieldCount, WORDS.field)} · {plural(t.taskCount, WORDS.task)}
                  </span>
                </span>
                {t.isDefault && <Badge kind="success">افتراضي</Badge>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* المحفظة: خطُّ العمل الذي ينتمي إليه المشروع.
          حين يُفتح المشروعُ من داخل محفظةٍ، تكون محفظتُه مثبَّتةً فلا يُعرَض الاختيار. */}
      {presetPortfolioId ? (
        <div className="field">
          <span className="label">المحفظة</span>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <Icon name="folder" size={15} />
            <b className="small">{presetPortfolioName ?? 'المحفظة الحالية'}</b>
          </div>
        </div>
      ) : (
        <label className="field">
          <span className="label">المحفظة</span>
          <select
            className="input"
            value={portfolioId}
            onChange={(e) => setPortfolioId(e.target.value)}
          >
            <option value="">
              {tplPortfolio ? `محفظة القالب (${tplPortfolio})` : 'بلا محفظة'}
            </option>
            {pfRows.map((pf) => (
              <option key={pf.id} value={pf.id}>
                {pf.name}
              </option>
            ))}
          </select>
          {pfRows.length === 0 && (
            <span className="tiny mute">
              لا محافظ بعد — <Link to="/agency/portfolios">أنشئ خطَّ عمل</Link> ليُقارَن المشروع
              بأقرانه.
            </span>
          )}
        </label>
      )}

      <label className="field">
        <span className="label">اسم المشروع</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="مثل: حملات شهر سبتمبر"
        />
      </label>

      <label className="field">
        <span className="label">وصف مختصر (اختياري)</span>
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="label">مالك المشروع</span>
          <select className="select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">أنا (المُنشئ)</option>
            {assignable.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <div className="field">
          <span className="label">اللون</span>
          <div className="ag-swatches">
            {PROJECT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`ag-swatch${color === c ? ' is-active' : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                aria-label={`اللون ${c}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
          {create.isPending ? 'جارٍ الإنشاء…' : 'أنشئ المشروع'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}
